import { useCallback, useEffect, useRef, useState } from 'react'
import { AnalyticsEvents, track } from 'v2/utils/analytics'
import type { ShownFeedbackPrompt } from './components/feedback-dialog'
import type { BuddyResponseSummary } from './use-buddy-chat'

export const FEEDBACK_PROMPT_DELAY_MS = 2000

export interface FeedbackPromptGates {
  isPanelOpen: boolean
  isChatBusy: boolean
  isComposerEmpty: boolean
  // False once the thread is cleared, as New chat does.
  hasConversation: boolean
  // The manual /ticket flow: its dialog is open or its submit is in flight.
  isTicketFlowActive: boolean
  // PostHog isBuddyFeedbackDisabled: stops prompts everywhere without a deploy.
  isBuddyFeedbackDisabled: boolean
}

// The single decision point for showing the prompt. How often a user is asked is capped
// server-side, which simply stops sending feedback_prompt.
const canOpen = (gates: FeedbackPromptGates): boolean =>
  gates.isPanelOpen &&
  !gates.isChatBusy &&
  gates.isComposerEmpty &&
  gates.hasConversation &&
  !gates.isTicketFlowActive &&
  !gates.isBuddyFeedbackDisabled

// Opens the feedback prompt a beat after a turn that asked for one. A gate shutting while
// it waits (a new send, closing the panel, typing, New chat, opening /ticket) cancels it
// for good.
export const useFeedbackPrompt = (gates: FeedbackPromptGates) => {
  // Kept after close so the dialog does not blank while it animates out.
  const [prompt, setPrompt] = useState<ShownFeedbackPrompt | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout>>()
  const allowed = canOpen(gates)
  // Read by the timer, which must see the latest render rather than the scheduling one.
  const allowedRef = useRef(allowed)
  allowedRef.current = allowed

  const cancel = useCallback(() => clearTimeout(timerRef.current), [])

  useEffect(() => {
    if (!allowed) cancel()
  }, [allowed, cancel])

  useEffect(() => cancel, [cancel])

  // Runs from onFinish, before the chat reports idle, so the gates are checked only once
  // the delay has elapsed.
  const schedule = useCallback(
    ({ feedbackPrompt, sessionId }: BuddyResponseSummary) => {
      cancel()
      if (!feedbackPrompt || !sessionId) return
      timerRef.current = setTimeout(() => {
        if (!allowedRef.current) return
        setPrompt({ ...feedbackPrompt, sessionId, feedbackId: crypto.randomUUID() })
        setIsOpen(true)
        track(AnalyticsEvents.BUDDY_FEEDBACK_PROMPT_SHOWN, {
          reason: feedbackPrompt.reason,
          session_id: sessionId,
        })
      }, FEEDBACK_PROMPT_DELAY_MS)
    },
    [cancel],
  )

  const close = useCallback(() => setIsOpen(false), [])

  return { prompt, isOpen, schedule, close }
}
